import { MAPCOLORS } from "./mapstyles";
import "../../util/theme/flags.css";
import acceptanceLevels from "../../util/lang/acceptanceLevels";
import isTouchDevice from "../../util/isTouchDevice";

export const assignAcceptanceMapData = (map, acceptanceData) => {
  if (!map || !acceptanceData) return;
  map.setMapTypeId("acceptance_map");
  map.data.forEach(function (feature) {
    const adm0_a3 = feature.getProperty("adm0_a3");
    const iso_a2 = feature.getProperty("iso_a2");
    // Match by 3-letter (adm0_a3) or 2-letter (iso_a2) so API data using "US" matches GeoJSON "USA"
    const foundCountry = acceptanceData.find(
      (d) => d.cca3 === adm0_a3 || d.cca3 === iso_a2
    );
    const credit_level = foundCountry?.credit_level ?? "NO COVERAGE";
    const translated_name = foundCountry?.translated_name ?? "";
    feature.setProperty("credit_level", credit_level);
    feature.setProperty("isActive", false);
    feature.setProperty("translated_name", translated_name);
  });
};

export const getInitialZoom = () => {
  const windowWidth = window.innerWidth;
  if (windowWidth > 2048) {
    return 4;
  } else if (windowWidth > 1024) {
    return 3;
  } else if (windowWidth > 512) {
    return 2;
  } else {
    return 2;
  }
};

/**
 * Sync map polygon selection to match selectedCountry (e.g. when set from search).
 * Call with countryCode when a country is selected, or null to clear selection.
 */
export const syncMapSelectionToCountry = (map, theme, countryCode) => {
  if (!map || !map.data) return;
  let feature = null;
  if (countryCode) {
    map.data.forEach((feat) => {
      if (feat.getProperty("adm0_a3") === countryCode) {
        feature = feat;
      }
    });
  }
  if (feature === activeFeature) {
    return;
  }
  map.data.revertStyle();
  if (activeFeature) {
    activeFeature.setProperty("isActive", false);
    map.data.overrideStyle(activeFeature, {
      fillColor:
        MAPCOLORS.default[activeFeature.getProperty("credit_level")],
    });
  }
  if (feature) {
    feature.setProperty("isActive", true);
    activeFeature = feature;
    map.data.overrideStyle(feature, {
      fillColor: theme.palette.secondary.light,
    });
  } else {
    activeFeature = null;
  }
};

export const makeCountryMap = (map, countryMapType, theme) => {
  const mapColors = MAPCOLORS.default;
  map.mapTypes.set("acceptance_map", countryMapType);
  map.data.setStyle((feature) => {
    const credit_level = feature.getProperty("credit_level");
    const isActive = feature.getProperty("isActive");

    // If active, use secondary.light color
    const fillColor = isActive
      ? theme.palette.secondary.light
      : mapColors[credit_level];

    const styleProps = {
      fillOpacity: 1,
      fillColor: fillColor,
      strokeColor: "#40404A",
      strokeWeight: 0.25,
    };
    return styleProps;
  });
};

const processPoints = (geometry, callback, thisArg) => {
  if (geometry instanceof window.google.maps.LatLng) {
    callback.call(thisArg, geometry);
  } else if (geometry instanceof window.google.maps.Data.Point) {
    callback.call(thisArg, geometry.get());
  } else {
    geometry.getArray().forEach(function (g) {
      processPoints(g, callback, thisArg);
    });
  }
};

// Centralized hover state for country polygons only (hover preview tooltip vs moving onto the tooltip UI).
class HoverManager {
  constructor() {
    this.hoveredFeature = null;
    this.hoverSource = null; // 'country' | 'tooltip'
    this.areaInfoWindow = null;
    this.closeTimeout = null;
    this.CLOSE_DELAY = 100; // ms delay before closing tooltip
    this.mousemoveListener = null; // Listener for updating tooltip position
    this.map = null; // Reference to map for mousemove listener
  }

  // Check if we should allow hover (modal not open)
  canHover(countryDetailsModalOpen) {
    if (countryDetailsModalOpen) return false;
    return true;
  }

  // Start hovering over a feature
  startHover(feature, source, createTooltipFn) {
    // Clear any pending close
    this.cancelClose();

    // If already hovering the same feature from same source, do nothing
    if (this.hoveredFeature === feature && this.hoverSource === source) {
      return;
    }

    // If hovering different feature, close current tooltip
    if (this.hoveredFeature !== feature && this.areaInfoWindow) {
      this.areaInfoWindow.close();
      this.areaInfoWindow = null;
    }

    // Set new hover state
    this.hoveredFeature = feature;
    this.hoverSource = source;

    // Create tooltip if it doesn't exist, or update position if switching sources for same feature
    if (!this.areaInfoWindow) {
      this.areaInfoWindow = createTooltipFn();
    }
    // If same feature + tooltip already open, hoverSource was updated above (e.g. country -> tooltip).
  }

  // Stop hovering (with optional delay)
  stopHover(source, immediate = false) {
    // Only stop if this source matches current hover source
    // This prevents one source from closing when another is still active
    if (this.hoverSource !== source) {
      return;
    }

    // If we're stopping this source but tooltip might still be needed by another source,
    // we'll let the timeout handle it. But since we check source match above, this should be fine.
    if (immediate) {
      this.closeTooltip();
    } else {
      this.scheduleClose();
    }
  }

  // Schedule tooltip to close after delay
  scheduleClose() {
    this.cancelClose();
    this.closeTimeout = setTimeout(() => {
      this.closeTooltip();
    }, this.CLOSE_DELAY);
  }

  // Cancel pending close
  cancelClose() {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
  }

  // Close tooltip immediately
  closeTooltip() {
    this.cancelClose();
    this.removeMousemoveListener();
    if (this.areaInfoWindow) {
      this.areaInfoWindow.close();
      this.areaInfoWindow = null;
    }
    this.hoveredFeature = null;
    this.hoverSource = null;
  }

  // Update tooltip position to follow cursor
  updatePosition(latLng) {
    if (this.areaInfoWindow && this.hoverSource !== "tooltip") {
      // Only update if not hovering over tooltip itself
      this.areaInfoWindow.setPosition(latLng);
    }
  }

  // Add mousemove listener to make tooltip follow cursor
  addMousemoveListener(map) {
    this.removeMousemoveListener(); // Remove any existing listener
    this.map = map;

    // Only add listener while pointer is on the map polygon (not while over the hover tooltip DOM)
    if (this.hoverSource === "country") {
      this.mousemoveListener = map.data.addListener("mousemove", (e) => {
        if (this.isHovering(e.feature) && this.hoverSource !== "tooltip") {
          this.updatePosition(e.latLng);
        }
      });
    }
  }

  // Remove mousemove listener
  removeMousemoveListener() {
    if (this.mousemoveListener && this.map) {
      window.google.maps.event.removeListener(this.mousemoveListener);
      this.mousemoveListener = null;
    }
  }

  // Check if currently hovering a specific feature
  isHovering(feature) {
    return this.hoveredFeature === feature;
  }

  // Get current hovered feature
  getHoveredFeature() {
    return this.hoveredFeature;
  }

  // Get current hover source
  getHoverSource() {
    return this.hoverSource;
  }
}

// Global hover manager instance
const hoverManager = new HoverManager();

//these variables keep track of the different windows on click and hover for the accc
let clickedAreaInfoWindow = null;
let activeFeature = null;
let dataFeatureClicked = false; // Flag to prevent map click from interfering with data click
let openCountryDetailsModal = null; // Function to open country details modal
let countryDetailsModalOpen = false; // Flag to track if modal is open
let modalSourceButton = null; // Reference to the button that opened the modal

export const getTooltipContent = (
  feature,
  showAcceptanceCopy,
  theme,
  isHoverTooltip = false
) => {
  // For hover tooltips, show only flag and country name
  if (isHoverTooltip) {
    return `
      <div class="acceptance-infowindow" style="font-family: ${theme.typography.fontFamily}; color: ${
        theme.palette.text.primary
      }" >
        <div class="headline"><div class="flag:${feature.getProperty(
          "iso_a2"
        )}"></div>
        <b>${
          feature.getProperty("translated_name") ?? feature.getProperty("name")
        }</b></div>
      </div>
      `;
  }

  // For clicked tooltips, show full content with acceptance copy and See Details button
  return `
      <div class="acceptance-infowindow ${
        showAcceptanceCopy && "large"
      }" style="font-family: ${theme.typography.fontFamily}; color: ${
        theme.palette.text.primary
      }" >
        <div class="headline"><div class="flag:${feature.getProperty(
          "iso_a2"
        )}"></div>
        <b>${
          feature.getProperty("translated_name") ?? feature.getProperty("name")
        }</b></div>
        <p style="color: ${theme.palette.text.secondary}; display: ${
          showAcceptanceCopy ? "block" : "none"
        }">${
          acceptanceLevels[theme.lang].tooltip[
            feature.getProperty("credit_level") || "NO COVERAGE"
          ]
        }</p>
  <div style="margin:auto; display: flex; justify-content: center; align-items: center;"><button class="see-details-button">View Details</button></div>
      </div>
      `;
};

// Helper function to attach click handlers to See Details buttons in InfoWindow
const attachSeeDetailsHandlers = (
  infoWindow,
  feature,
  map,
  theme,
  setSelectedCountry,
  activeTooltip,
  tooltipFocusTargetRef
) => {
  // Always attach handlers - button should always be functional
  // The modal function check will happen when button is clicked

  // Extract country data from feature (do this once, outside the event listener)
  const countryData = {
    name: feature.getProperty("name"),
    translated_name: feature.getProperty("translated_name"),
    code: feature.getProperty("adm0_a3"),
    credit_level: feature.getProperty("credit_level"),
    iso_a2: feature.getProperty("iso_a2"),
    region: feature.getProperty("region_wb"),
  };

  let handlerAttached = false; // Track if handler has been attached to prevent duplicates

  // Function to find and attach handler to the button
  const findAndAttachHandler = () => {
    if (handlerAttached) return; // Prevent duplicate attachments
    // Get all InfoWindow containers and find the one that matches this InfoWindow
    // We'll identify it by checking if it contains the feature name we're looking for
    const countryName =
      feature.getProperty("translated_name") || feature.getProperty("name");
    const allContainers = document.querySelectorAll(".gm-style-iw-d");
    let infoWindowContainer = null;

    // Find the container that contains this feature's name
    for (const container of allContainers) {
      const headline = container.querySelector(".headline b");
      if (headline && headline.textContent.trim() === countryName) {
        infoWindowContainer = container;
        break;
      }
    }

    // Fallback: if we can't find by name, use the last opened one (most recent)
    if (!infoWindowContainer && allContainers.length > 0) {
      infoWindowContainer = allContainers[allContainers.length - 1];
    }

    if (infoWindowContainer) {
      const button = infoWindowContainer.querySelector(".see-details-button");

      if (button) {
        // Remove any existing listeners to avoid duplicates
        const newButton = button.cloneNode(true);
        button.parentNode.replaceChild(newButton, button);

        if (tooltipFocusTargetRef) {
          tooltipFocusTargetRef.current = newButton;
        }

        newButton.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();

          // Check if country is active, if not activate it
          if (!feature.getProperty("isActive")) {
            // Create a mock event object similar to what countryClicked expects
            let bounds = new window.google.maps.LatLngBounds();
            processPoints(feature.getGeometry(), bounds.extend, bounds);
            const mockEvent = {
              feature: feature,
              latLng: bounds.getCenter(),
            };
            countryClicked(
              map,
              setSelectedCountry,
              activeTooltip,
              theme,
              mockEvent,
              null,
              tooltipFocusTargetRef
            );
            // After countryClicked, update infoWindow reference if a new clicked tooltip was created
            if (clickedAreaInfoWindow != null) {
              infoWindow = clickedAreaInfoWindow;
            }
          }

          // Check if this is a different country than what is currently active
          const isDifferentCountry =
            activeFeature != null && activeFeature !== feature;

          // If it's a different country, close the tooltip for the currently active country
          if (isDifferentCountry) {
            // Close the clicked tooltip if it exists and is for the active country
            if (clickedAreaInfoWindow != null) {
              clickedAreaInfoWindow.close();
              clickedAreaInfoWindow = null;
            }
            // Close hover tooltip if it exists
            hoverManager.closeTooltip();
          }

          // Store references to keep tooltip open and restore focus later
          modalSourceButton = newButton;
          countryDetailsModalOpen = true;

          // Don't close the current InfoWindow - keep it open
          // infoWindow.close(); // REMOVED

          // Open the modal - always try to open it
          if (window.openCountryDetailsModal) {
            window.openCountryDetailsModal(countryData);
          } else if (openCountryDetailsModal) {
            openCountryDetailsModal(countryData);
          } else {
            console.warn("Country details modal function not available");
          }
        });
        handlerAttached = true; // Mark as attached
        return true; // Successfully attached
      } else {
        // If button not found immediately, try again after a short delay
        setTimeout(findAndAttachHandler, 50);
        return false;
      }
    } else {
      // If container not found, try again after a short delay
      setTimeout(findAndAttachHandler, 50);
      return false;
    }
  };

  // Use domready event to ensure the InfoWindow content is loaded
  window.google.maps.event.addListenerOnce(infoWindow, "domready", () => {
    findAndAttachHandler();
  });

  // Fallback: Also try attaching after a short delay in case domready doesn't fire
  // This ensures the button handlers are attached even if domready event has issues
  setTimeout(() => {
    findAndAttachHandler();
  }, 100);
};

export const countryView_addListeners = (
  map,
  theme,
  setSelectedCountry,
  activeTooltip,
  openModalFunction,
  tooltipFocusTargetRef
) => {
  // Store the modal function for use in InfoWindow handlers
  openCountryDetailsModal = openModalFunction;

  // Interaction model (country view only):
  // - Click polygon: opens pinned InfoWindow + selection (data click). dataFeatureClicked avoids the map
  //   background click firing in the same gesture and clearing that state.
  // - Click map (not polygon): closes pinned tooltip, clears selection, zooms out.
  // - Hover polygon (non-touch): light InfoWindow follows cursor until mouse leaves polygon or moves onto tooltip.
  map.addListener("click", function () {
    // Don't close tooltip if modal is open
    if (countryDetailsModalOpen) {
      return;
    }

    // If a data feature was clicked, don't reset - let the data click handler handle it
    if (dataFeatureClicked) {
      dataFeatureClicked = false;
      return;
    }

    if (clickedAreaInfoWindow != null) {
      clickedAreaInfoWindow.close();
      clickedAreaInfoWindow = null;
      if (tooltipFocusTargetRef) {
        tooltipFocusTargetRef.current = null;
      }
      if (activeFeature) {
        activeFeature.setProperty("isActive", false);
        // Reset color to default by reverting the override
        map.data.revertStyle(activeFeature);
        // Reapply default style based on credit_level
        map.data.overrideStyle(activeFeature, {
          fillColor:
            MAPCOLORS.default[activeFeature.getProperty("credit_level")],
        });
        activeFeature = null;
      }
      setSelectedCountry(null);
      activeTooltip.current = null;
    }
    map.setZoom(map.minZoom);
  });

  map.data.addListener("click", function (e) {
    // Set flag to prevent map click handler from interfering
    dataFeatureClicked = true;
    countryClicked(
      map,
      setSelectedCountry,
      activeTooltip,
      theme,
      e,
      null,
      tooltipFocusTargetRef
    );
    // Reset flag after a short delay to allow the click to complete
    setTimeout(() => {
      dataFeatureClicked = false;
    }, 100);
  });

  if (!isTouchDevice()) {
    // Use mouseenter instead of mousemove for more reliable hover detection
    map.data.addListener("mouseover", function (e) {
      if (!hoverManager.canHover(countryDetailsModalOpen)) {
        return;
      }

      // Don't show tooltip if feature is active
      if (e.feature.getProperty("isActive")) {
        return;
      }

      // If pointer moved from polygon onto the hover tooltip, source is "tooltip" — don't open a second tooltip
      if (
        hoverManager.isHovering(e.feature) &&
        hoverManager.getHoverSource() === "tooltip"
      ) {
        return;
      }

      // Start hover from the polygon
      hoverManager.startHover(e.feature, "country", () => {
        let bounds = new window.google.maps.LatLngBounds();
        processPoints(e.feature.getGeometry(), bounds.extend, bounds);
        const infoWindow = new window.google.maps.InfoWindow({
          content: getTooltipContent(e.feature, true, theme, true), // isHoverTooltip = true
          pixelOffset: new window.google.maps.Size(0, -5),
        });
        infoWindow.setPosition(e.latLng);
        infoWindow.open(map);

        // Add mousemove listener to make tooltip follow cursor
        hoverManager.addMousemoveListener(map);

        // Attach tooltip hover handlers
        window.google.maps.event.addListenerOnce(infoWindow, "domready", () => {
          const infoWindowElement = document.querySelector(".gm-style-iw-d");
          if (infoWindowElement) {
            infoWindowElement.addEventListener("mouseenter", () => {
              // Keep tooltip open when hovering over it
              // Remove mousemove listener when hovering over tooltip itself
              hoverManager.removeMousemoveListener();
              hoverManager.startHover(e.feature, "tooltip", () => infoWindow);
            });
            infoWindowElement.addEventListener("mouseleave", () => {
              // Schedule close when leaving tooltip
              // Restore mousemove listener when leaving tooltip
              hoverManager.stopHover("tooltip");
              // If still hovering the feature, restore mousemove listener
              if (hoverManager.isHovering(e.feature)) {
                hoverManager.addMousemoveListener(map);
              }
            });
          }
        });

        // No See Details button for hover tooltips - only show flag and country name

        return infoWindow;
      });
    });

    map.data.addListener("mouseout", function (e) {
      if (!hoverManager.canHover(countryDetailsModalOpen)) {
        return;
      }

      // Only schedule close when leaving the polygon while still in "country" hover (not from the tooltip DOM)
      if (
        hoverManager.isHovering(e.feature) &&
        hoverManager.getHoverSource() === "country"
      ) {
        hoverManager.stopHover("country");
      }
    });
  }
};

export function countryClicked(
  map,
  setSelectedCountry,
  activeTooltip,
  theme,
  e,
  feat,
  tooltipFocusTargetRef
) {
  //if clicking into a new country repostion the map and pan
  const feature = e ? e.feature : feat;

  // If clicking the same country that's already active and tooltip is still open, just update tooltip position
  const tooltipStillOpen =
    clickedAreaInfoWindow != null && clickedAreaInfoWindow.getMap() != null;
  if (activeFeature === feature && tooltipStillOpen) {
    let bounds = new window.google.maps.LatLngBounds();
    processPoints(feature.getGeometry(), bounds.extend, bounds);
    clickedAreaInfoWindow.setPosition(e ? e.latLng : bounds.getCenter());
    return;
  }

  let bounds = new window.google.maps.LatLngBounds();
  processPoints(feature.getGeometry(), bounds.extend, bounds);

  // Close any hover tooltip
  hoverManager.closeTooltip();
  activeTooltip.current = null;
  if (tooltipFocusTargetRef) {
    tooltipFocusTargetRef.current = null;
  }

  if (!feature.getProperty("isActive")) {
    //https://stackoverflow.com/questions/3817812/google-maps-v3-can-i-ensure-smooth-panning-every-time
    var panPath = []; // An array of points the current panning action will use
    var panQueue = []; // An array of subsequent panTo actions to take
    var STEPS = 5; // The number of steps that each panTo action will undergo

    function panTo(newLat, newLng) {
      if (panPath.length > 0) {
        // We are already panning...queue this up for next move
        panQueue.push([newLat, newLng]);
      } else {
        // Lets compute the points we'll use
        panPath.push("LAZY SYNCRONIZED LOCK"); // make length non-zero - 'release' this before calling setTimeout
        var curLat = map.getCenter().lat();
        var curLng = map.getCenter().lng();
        var dLat = (newLat - curLat) / STEPS;
        var dLng = (newLng - curLng) / STEPS;

        for (var i = 0; i < STEPS; i++) {
          panPath.push([curLat + dLat * i, curLng + dLng * i]);
        }
        panPath.push([newLat, newLng]);
        panPath.shift(); // LAZY SYNCRONIZED LOCK
        setTimeout(doPan, 20);
      }
    }

    function doPan() {
      var next = panPath.shift();
      if (next != null) {
        // Continue our current pan action
        map.panTo(new window.google.maps.LatLng(next[0], next[1]));
        setTimeout(doPan, 20);
      } else {
        // We are finished with this pan - check if there are any queue'd up locations to pan to
        var queued = panQueue.shift();
        if (queued != null) {
          panTo(queued[0], queued[1]);
        }
      }
    }
    //figure out if bounds are in view in here too - LATER REFINEMENT IF TIME
    const countryCode = feature.getProperty("adm0_a3");
    const countryName =
      feature.getProperty("translated_name") || feature.getProperty("name");

    // Special handling for Antarctica - use a specific zoom and center to show it at reasonable size
    if (countryCode === "ATA" || countryName === "Antarctica") {
      // Center on Antarctica at a reasonable zoom level (zoom 3 shows it at a good size)
      map.setCenter(new window.google.maps.LatLng(-75, 0));
      map.setZoom(3);
    } else {
      const center = bounds.getCenter();
      panTo(center.lat(), center.lng());
    }
  }

  map.data.revertStyle();
  if (activeFeature) {
    activeFeature.setProperty("isActive", false);
    // Reapply default style to previously active feature
    map.data.overrideStyle(activeFeature, {
      fillColor:
        MAPCOLORS.default[activeFeature.getProperty("credit_level")],
    });
  }
  feature.setProperty("isActive", true);
  activeFeature = feature;

  // Apply secondary.light color to the clicked feature
  map.data.overrideStyle(feature, {
    fillColor: theme.palette.secondary.light,
  });

  if (clickedAreaInfoWindow != null) {
    clickedAreaInfoWindow.close();
    clickedAreaInfoWindow = null;
  }

  // Close any active tooltip reference
  if (activeTooltip?.current) {
    activeTooltip.current.close();
    activeTooltip.current = null;
  }

  // Create new clicked tooltip
  clickedAreaInfoWindow = new window.google.maps.InfoWindow({
    content: getTooltipContent(feature, true, theme),
  });
  clickedAreaInfoWindow.setPosition(e ? e.latLng : bounds.getCenter());
  clickedAreaInfoWindow.open(map);
  activeTooltip.current = clickedAreaInfoWindow;

  window.google.maps.event.addListenerOnce(
    clickedAreaInfoWindow,
    "closeclick",
    () => {
      clickedAreaInfoWindow = null;
      if (tooltipFocusTargetRef) {
        tooltipFocusTargetRef.current = null;
      }
    }
  );

  // Scroll to search input so user can easily select another country (-20px offset)
  setTimeout(() => {
    const searchInput = document.getElementById("country-search-input");
    if (searchInput) {
      const rect = searchInput.getBoundingClientRect();
      const targetY = window.scrollY + rect.top - 20;
      window.scrollTo({ top: Math.max(0, targetY), behavior: "smooth" });
    }
  }, 100);

  // Attach click handler to See Details button
  attachSeeDetailsHandlers(
    clickedAreaInfoWindow,
    feature,
    map,
    theme,
    setSelectedCountry,
    activeTooltip,
    tooltipFocusTargetRef
  );

  setSelectedCountry({
    code: feature.getProperty("adm0_a3"),
    region: feature.getProperty("region_wb"),
  });
}

// Function to handle modal close - restores focus and re-enables hovers
export const handleCountryDetailsModalClose = () => {
  countryDetailsModalOpen = false;

  // Restore focus to the button that opened the modal
  if (modalSourceButton) {
    // Use setTimeout to ensure modal is fully closed before focusing
    setTimeout(() => {
      try {
        modalSourceButton.focus();
      } catch (e) {
        // Button might not be in DOM anymore, that's okay
        console.log("Could not restore focus to button:", e);
      }
    }, 100);
  }

  // Clear references
  modalSourceButton = null;
};

// Clear any selected/active country state and close tooltips
export const resetCountrySelection = (map, activeTooltip) => {
  // Close hover tooltip
  hoverManager.closeTooltip();

  if (clickedAreaInfoWindow) {
    clickedAreaInfoWindow.close();
    clickedAreaInfoWindow = null;
  }

  if (map?.data) {
    map.data.revertStyle();
  }

  if (activeFeature) {
    activeFeature.setProperty("isActive", false);
    activeFeature = null;
  }

  dataFeatureClicked = false;
  countryDetailsModalOpen = false;
  modalSourceButton = null;

  if (activeTooltip?.current?.close) {
    activeTooltip.current.close();
  }
  if (activeTooltip) {
    activeTooltip.current = null;
  }
};

export const createCustomZoomControl = (map, google) => {
  var zoomControlDiv = document.createElement("div");
  var zoomControl = new ZoomControl(zoomControlDiv, map);

  function ZoomControl(controlDiv, map) {
    //is there a better way to do this?
    const controlWrapper = document.createElement("div");
    const zoomOutButton = document.createElement("button");
    const zoomInButton = document.createElement("button");

    zoomControlDiv.classList.add("zoom-control");
    zoomInButton.innerHTML = "&plus;";
    zoomInButton.setAttribute("aria-label", "Zoom In");
    zoomOutButton.innerHTML = "&minus;";
    zoomOutButton.setAttribute("aria-label", "Zoom Out");
    controlWrapper.appendChild(zoomInButton);
    controlWrapper.appendChild(zoomOutButton);
    controlDiv.appendChild(controlWrapper);

    google.maps.event.addDomListener(zoomInButton, "click", function () {
      map.setZoom(map.getZoom() + 1);
    });
    google.maps.event.addDomListener(zoomOutButton, "click", function () {
      map.setZoom(map.getZoom() - 1);
    });
  }
  zoomControlDiv.index = 1;
  map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(zoomControlDiv);
  return zoomControl;
};
